/**
 * Who a commit's work came from, read from its `Co-authored-by:` trailers and
 * the branch it sits on.
 *
 * The author of a commit is always the person whose repository it is (see
 * AGENTS.md, "Attribution"); the trailers say who acted on their behalf. Four
 * answers, in this order of precedence:
 *
 *   member / specialist — an agent the workbench ran. Its trailer is
 *     `Name <name@<team>.agents.mojoup.local>` — a crew member's (D178)
 *     carries its plan as a sub-address, `Name <name+plan-2@<team>…>` — (the older
 *     `Name <name@mojoup.workbench>` is still read), or its commit sits on
 *     `mojoup/<team>/<member>`, or the trailer names a member of a known team.
 *     Workbench agents that run inside Claude Code carry Claude's trailer too;
 *     they still read as the member.
 *   external-ai — an AI trailer alone (Claude, Copilot, Codex, Gemini, …): an
 *     agent the workbench did not run.
 *   person — no AI trailer at all.
 *
 * `workbench` is the app's own housekeeping commit (memory refresh, recovery
 * auto-commit): neither a person nor an agent anyone asked for.
 *
 * Shared by the host (which classifies the graph it sends) and the renderer
 * (which filters on it). No I/O.
 */
/** The domain of the workbench's own agent addresses (`src/git/gitExec.ts` `coAuthoredBy`). */
export const AGENT_TRAILER_DOMAIN = "agents.mojoup.local";
/** The name the workbench signs its own housekeeping commits with. */
export const WORKBENCH_TRAILER_NAME = "Mojo Up AI Workbench";
/** `Name <email>` → parts. A value without an address keeps its name. */
export function parseCoAuthor(value) {
    const text = value.replace(/^co-authored-by:\s*/i, "").trim();
    if (!text)
        return undefined;
    const m = /^(.*?)\s*<([^>]*)>\s*$/.exec(text);
    if (!m)
        return { name: text, email: "" };
    return { name: m[1].trim(), email: m[2].trim().toLowerCase() };
}
/** Lower-case, non-alphanumerics to `-`: the slug `coAuthoredBy` and the branch names use. */
export function attributionSlug(value) {
    return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
/** The name before a parenthesised model: `Shakespeare (Claude Opus 5.5)` → `Shakespeare`. */
function bareName(name) {
    return name.replace(/\s*\(.*\)\s*$/, "").trim();
}
/** Which AI tool a trailer is from, or undefined for a person. */
export function aiTool(author) {
    const name = author.name.toLowerCase();
    const email = author.email;
    if (/@anthropic\.com$/.test(email) || /^claude\b/.test(name))
        return "Claude";
    if (/copilot/.test(email) || /^(github )?copilot\b/.test(name))
        return "Copilot";
    if (/@openai\.com$/.test(email) || /\bcodex\b/.test(name) || /^(chat)?gpt\b/.test(name))
        return "Codex";
    if (/gemini/.test(email) || /^gemini\b/.test(name) || /^jules\b/.test(name))
        return "Gemini";
    if (/@cursor\.(com|sh)$/.test(email) || /^cursor( agent)?\b/.test(name))
        return "Cursor";
    if (/^aider\b/.test(name) || /@aider\.chat$/.test(email))
        return "Aider";
    if (/@(devin\.ai|cognition\.ai)$/.test(email) || /^devin\b/.test(name))
        return "Devin";
    if (/^(opencode|kilo ?code|cline|amp|windsurf|antigravity)\b/.test(name))
        return name.split(/[\s(<]/)[0].replace(/^./, (c) => c.toUpperCase());
    return undefined;
}
function findMember(ctx, name, teamSlug) {
    const wanted = attributionSlug(bareName(name));
    if (!wanted)
        return undefined;
    const teams = ctx?.teams ?? [];
    // A team named in the address or the branch narrows the search; a name
    // alone (the older trailer) looks everywhere, first match wins.
    const scoped = teamSlug ? teams.filter((t) => attributionSlug(t.id) === teamSlug || attributionSlug(t.name) === teamSlug) : teams;
    for (const team of scoped) {
        const member = team.members.find((m) => attributionSlug(m.name) === wanted);
        if (member)
            return { team, name: member.name, avatar: member.avatar };
        // A specialist is a team of one whose member may carry the specialist's name.
        if (team.kind === "specialist" && (attributionSlug(team.name) === wanted || attributionSlug(team.id) === wanted)) {
            return { team, name: team.members[0]?.name ?? team.name, avatar: team.members[0]?.avatar };
        }
    }
    return undefined;
}
function memberAttribution(hit, fallback) {
    const team = hit?.team;
    const out = {
        kind: team?.kind === "specialist" ? "specialist" : "member",
        name: hit?.name ?? fallback.name,
    };
    const teamId = team?.id ?? fallback.teamId;
    if (teamId)
        out.teamId = teamId;
    if (team)
        out.teamName = team.name;
    if (hit?.avatar !== undefined)
        out.avatar = hit.avatar;
    if (fallback.plan)
        out.plan = fallback.plan;
    return out;
}
/** The plan in a crew member's address (`mira+plan-2@…` → `PLAN-2`), else undefined. */
export function trailerPlan(email) {
    const local = email.split("@")[0] ?? "";
    const plus = local.indexOf("+");
    const plan = plus > 0 ? local.slice(plus + 1) : "";
    return plan ? plan.toUpperCase() : undefined;
}
/** A trailer's name without a crew member's plan: `Mira·PLAN-2` → `Mira`. */
function withoutPlan(name) {
    const i = name.lastIndexOf("·");
    return i > 0 ? name.slice(0, i) : name;
}
/**
 * `mojoup/<team>/<member>` → its parts, and `mojoup/<team>/<member>@<plan-slug>`
 * (a member's branch for one plan, D178) with the plan's slug; undefined for
 * any other branch (integration branches included).
 */
export function workbenchBranch(branch) {
    const m = /^mojoup\/([^/]+)\/([^/@]+)(?:@([^/@]+))?$/.exec(branch.replace(/^refs\/heads\//, ""));
    if (!m || m[1] === "integration")
        return undefined;
    return { teamSlug: m[1], member: m[2], ...(m[3] ? { planSlug: m[3] } : {}) };
}
/** Classify one commit. Precedence: workbench member or specialist > external AI > person. */
export function attributeCommit(input, ctx) {
    const authors = input.coAuthors.map(parseCoAuthor).filter((a) => !!a);
    for (const a of authors) {
        // The address the workbench writes: `<member>@<team>.agents.mojoup.local`.
        const domain = a.email.split("@")[1] ?? "";
        if (domain.endsWith(`.${AGENT_TRAILER_DOMAIN}`)) {
            if (bareName(a.name) === WORKBENCH_TRAILER_NAME)
                continue;
            const teamSlug = domain.slice(0, -(AGENT_TRAILER_DOMAIN.length + 1));
            const name = withoutPlan(a.name);
            const hit = findMember(ctx, name, teamSlug) ?? findMember(ctx, name);
            const plan = trailerPlan(a.email);
            return memberAttribution(hit, { name: bareName(name), teamId: teamSlug === "solo" ? undefined : teamSlug, ...(plan ? { plan } : {}) });
        }
        // The older address: `<member>@mojoup.workbench` — no team in it.
        if (domain === "mojoup.workbench")
            return memberAttribution(findMember(ctx, a.name), { name: bareName(a.name) });
    }
    // A trailer naming a member of a known team, whatever its address (an agent
    // that wrote `Shakespeare <noreply@anthropic.com>`).
    for (const a of authors) {
        const hit = findMember(ctx, a.name);
        if (hit)
            return memberAttribution(hit, { name: hit.name });
    }
    // The branch the workbench made for the member.
    for (const branch of input.branches ?? []) {
        const wb = workbenchBranch(branch);
        if (!wb)
            continue;
        const hit = findMember(ctx, wb.member, wb.teamSlug);
        return memberAttribution(hit, { name: hit?.name ?? wb.member, teamId: wb.teamSlug, ...(wb.planSlug ? { plan: wb.planSlug } : {}) });
    }
    for (const a of authors) {
        const tool = aiTool(a);
        if (tool)
            return { kind: "external-ai", name: a.name || tool, tool };
    }
    if (authors.some((a) => bareName(a.name) === WORKBENCH_TRAILER_NAME))
        return { kind: "workbench", name: "AI Workbench" };
    return { kind: "person", name: input.author || "Unknown" };
}
/** Two letters for a person's marker: "Jean-Pierre Simonis" → "JS", "jp" → "JP". */
export function initials(name) {
    const words = name.trim().split(/[\s._@-]+/).filter(Boolean);
    if (words.length === 0)
        return "?";
    if (words.length === 1)
        return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}
/** The task a commit's subject names: `T-17: …` → `T-17`. */
export function subjectTaskId(subject) {
    return /^(T-\d+)\s*:/.exec(subject.trim())?.[1];
}
/** Whether a commit passes the filter. `team` is the workbench's agents, of `teamId` when one is given. */
export function matchesAttribution(a, filter, teamId) {
    if (filter === "everyone")
        return true;
    const kind = a?.kind ?? "person";
    if (filter === "people")
        return kind === "person";
    if (filter === "outside")
        return kind === "external-ai";
    if (kind !== "member" && kind !== "specialist")
        return false;
    return !teamId || a?.teamId === teamId;
}
//# sourceMappingURL=commitAttribution.js.map